import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Langs, { Localize } from "../../app/lang/langs";
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Slide from '@mui/material/Slide';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';

import { fetchCollabPendingAwareness, fetchCollabAwarenessHistory, fetchCollabPendingQuizzes, fetchCollabQuizHistory, fetchAwarenessTeams } from '../../components/vsecurite/vsecuriteSlice';
import { fetchAwarenessCampaigns } from '../../components/vsecurite/vsecuriteSlice';
import { fetchQuizzes } from '../../components/vsecurite/vsecuriteSlice';
import { fetchVsecAdmins, grantVsecAdmin, revokeVsecAdmin } from '../../components/vsecurite/vsecuriteSlice';
import { showSuccessToast, showErrorToast, checkPermission } from '../../app/utils/helpers';

// Admin — Awareness
import AwarenessList from '../../components/vsecurite/awareness/AwarenessList';
import AwarenessForm from '../../components/vsecurite/awareness/AwarenessForm';
import AwarenessTracking from '../../components/vsecurite/awareness/AwarenessTracking';

// Admin — Quiz
import QuizList from '../../components/vsecurite/quiz/QuizList';
import QuizForm from '../../components/vsecurite/quiz/QuizForm';
import QuestionForm from '../../components/vsecurite/quiz/QuestionForm';
import QuizQuestionsList from '../../components/vsecurite/quiz/QuizQuestionsList';
import QuizTracking from '../../components/vsecurite/quiz/QuizTracking';
import QuizImport from '../../components/vsecurite/quiz/QuizImport';

// Admin — Dashboard
import VSecuriteDashboard from '../../components/vsecurite/dashboard/VSecuriteDashboard';

// Collab — Awareness
import AwarenessCollab from '../../components/vsecurite/awareness/AwarenessCollab';
import AwarenessHistory from '../../components/vsecurite/awareness/AwarenessHistory';

// Collab — Quiz
import QuizCollab from '../../components/vsecurite/quiz/QuizCollab';
import QuizHistory from '../../components/vsecurite/quiz/QuizHistory';
import QuizReview from '../../components/vsecurite/quiz/QuizReview';
import QuizTake from '../../components/vsecurite/quiz/QuizTake';
import { config } from '../../app/utils/config';

const SlideUp = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const BRAND_ORANGE = '#FF9015';
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

// ─── VsecAdminDialog ─────────────────────────────────────────────────────────

/**
 * Dialog (full-screen on mobile) for superadmins to grant/revoke the
 * V-Sécurité admin permission to any collab.
 * Scope: vsecurite / type: edit / id_team: null (global)
 */
function VsecAdminDialog({ open, onClose, lang }) {
    const dispatch = useDispatch();
    const isMobile = useSelector((state) => state.settings.isMobile);
    const isMobileScreen = isMobile === 2;
    const admins  = useSelector((s) => s.vsecurite.admins.list);
    const collabs = useSelector((s) => s.vsecurite.admins.collabs);
    const loading = useSelector((s) => s.vsecurite.admins.loading);

    const [selected, setSelected] = useState(null);
    const [granting, setGranting] = useState(false);
    const [revoking, setRevoking] = useState(null); // id_collab being revoked

    useEffect(() => {
        if (open) dispatch(fetchVsecAdmins());
    }, [open, dispatch]);

    const handleGrant = async () => {
        if (!selected) return;
        setGranting(true);
        try {
            const res = await dispatch(grantVsecAdmin({ id_collab: selected.id })).unwrap();
            if (res?.responseData?.status === 1) {
                showSuccessToast(Localize('VSEC_ADMIN_GRANT_SUCCESS', lang));
                setSelected(null);
            } else {
                showErrorToast(Localize('VSEC_ADMIN_GRANT_ERROR', lang));
            }
        } catch {
            showErrorToast(Localize('VSEC_ADMIN_GRANT_ERROR', lang));
        } finally {
            setGranting(false);
        }
    };

    const handleRevoke = async (idCollab) => {
        setRevoking(idCollab);
        try {
            const res = await dispatch(revokeVsecAdmin({ id_collab: idCollab })).unwrap();
            if (res?.responseData?.status === 1) {
                showSuccessToast(Localize('VSEC_ADMIN_REVOKE_SUCCESS', lang));
            } else {
                showErrorToast(Localize('VSEC_ADMIN_REVOKE_ERROR', lang));
            }
        } catch {
            showErrorToast(Localize('VSEC_ADMIN_REVOKE_ERROR', lang));
        } finally {
            setRevoking(null);
        }
    };

    // Collabs not already admins
    const adminIds = new Set(admins.map((a) => a.id_collab));
    const options = collabs.filter((c) => !adminIds.has(c.id));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobileScreen}
            slots={isMobileScreen ? { transition: SlideUp } : undefined}
            slotProps={{ paper: { sx: { borderRadius: isMobileScreen ? 0 : '16px', minWidth: isMobileScreen ? undefined : 480, maxWidth: 560, width: '100%', zIndex: 1400 } } }}
            sx={{ zIndex: 1400 }}
        >
            {/* Header */}
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1, pr: 1.5 }}>
                <AdminPanelSettingsRoundedIcon sx={{ color: BRAND_ORANGE, fontSize: 24 }} />
                <Typography sx={{ flex: 1, fontWeight: 700, fontSize: '1rem', color: '#1a1a2e' }}>
                    <Langs str="VSEC_ADMIN_DIALOG_TITLE" />
                </Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: '#888', minWidth: 44, minHeight: 44 }}>
                    <CloseRoundedIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2, pb: 3, px: isMobileScreen ? 2 : 3 }}>

                {/* ── Add new admin ── */}
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'rgba(0,0,0,0.45)', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                    <Langs str="VSEC_ADMIN_ADD_SECTION" />
                </Typography>
                <Box sx={{
                    display: 'flex',
                    flexDirection: isMobileScreen ? 'column' : 'row',
                    gap: 1.5,
                    alignItems: isMobileScreen ? 'stretch' : 'flex-start',
                    mb: 3,
                }}>
                    <Autocomplete
                        sx={{ flex: 1 }}
                        options={options}
                        value={selected}
                        onChange={(_, v) => setSelected(v)}
                        getOptionLabel={(o) => `${o.prenom} ${o.nom}`}
                        componentsProps={{
                            popper: { sx: { zIndex: 1500 } },
                        }}
                        renderOption={(props, o) => (
                            <Box component="li" {...props} key={o.id} sx={{ gap: 1.5, minHeight: 48 }}>
                                <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: BRAND_ORANGE }}>
                                    {o.prenom?.[0]}{o.nom?.[0]}
                                </Avatar>
                                <Box>
                                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.2 }}>{o.prenom} {o.nom}</Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>{o.email}</Typography>
                                </Box>
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder={Localize('VSEC_ADMIN_SEARCH_PLACEHOLDER', lang)}
                                sx={{
                                    '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '16px' },
                                    '& input': { fontSize: '16px' },
                                }}
                            />
                        )}
                        noOptionsText={Localize('VSEC_ADMIN_NO_OPTIONS', lang)}
                        loading={loading}
                    />
                    <Tooltip title={Localize('VSEC_ADMIN_GRANT_BTN', lang)}>
                        <span style={{ width: isMobileScreen ? '100%' : 'auto' }}>
                            <Button
                                variant="contained"
                                onClick={handleGrant}
                                disabled={!selected || granting}
                                fullWidth={isMobileScreen}
                                startIcon={granting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PersonAddRoundedIcon />}
                                sx={{
                                    bgcolor: BRAND_ORANGE,
                                    '&:hover': { bgcolor: '#e07d0d' },
                                    '&:disabled': { bgcolor: 'rgba(255,144,21,0.4)' },
                                    borderRadius: '10px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    boxShadow: 'none',
                                    whiteSpace: 'nowrap',
                                    minWidth: 44,
                                    minHeight: 48,
                                    px: isMobileScreen ? 2 : 2,
                                }}
                            >
                                <Langs str="VSEC_ADMIN_GRANT_BTN" />
                            </Button>
                        </span>
                    </Tooltip>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* ── Current admins ── */}
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'rgba(0,0,0,0.45)', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                    <Langs str="VSEC_ADMIN_CURRENT_SECTION" />
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} sx={{ color: BRAND_ORANGE }} />
                    </Box>
                ) : admins.length === 0 ? (
                    <Typography sx={{ color: '#aaa', fontSize: '0.875rem', fontStyle: 'italic', py: 2, textAlign: 'center' }}>
                        <Langs str="VSEC_ADMIN_EMPTY" />
                    </Typography>
                ) : (
                    <List disablePadding>
                        {admins.map((a, i) => (
                            <ListItem
                                key={a.id_collab}
                                disablePadding
                                sx={{
                                    py: 0.75,
                                    px: 0,
                                    minHeight: 56,
                                    borderBottom: i < admins.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                                }}
                                secondaryAction={
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRevoke(a.id_collab)}
                                        disabled={revoking === a.id_collab}
                                        sx={{ color: '#d32f2f', '&:hover': { bgcolor: 'rgba(211,47,47,0.06)' }, minWidth: 44, minHeight: 44 }}
                                    >
                                        {revoking === a.id_collab
                                            ? <CircularProgress size={16} sx={{ color: '#d32f2f' }} />
                                            : <PersonRemoveRoundedIcon fontSize="small" />
                                        }
                                    </IconButton>
                                }
                            >
                                <ListItemAvatar sx={{ minWidth: 44 }}>
                                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: BRAND_ORANGE }}>
                                        {a.prenom?.[0]}{a.nom?.[0]}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={<Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1a1a2e' }}>{a.prenom} {a.nom}</Typography>}
                                    secondary={<Typography sx={{ fontSize: '0.75rem', color: '#888' }}>{a.email}</Typography>}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
        </Dialog>
    );
}

/** Helper: renders a MUI Tabs + Tab bar styled with the brand orange indicator.
 *  On mobile, tabs are scrollable to avoid overflow. */
function StyledTabs({ value, onChange, tabs, isMobileScreen }) {
    return (
        <Tabs
            value={value}
            onChange={onChange}
            variant={isMobileScreen ? 'scrollable' : 'standard'}
            scrollButtons={isMobileScreen ? 'auto' : false}
            allowScrollButtonsMobile
            sx={{
                borderBottom: '1px solid rgba(0, 40, 128, 0.08)',
                mb: 2.5,
                background: 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderRadius: '14px 14px 0 0',
                '& .MuiTabs-indicator': {
                    backgroundColor: BRAND_ORANGE,
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                    boxShadow: '0 0 8px rgba(255, 144, 21, 0.35)',
                },
                '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: isMobileScreen ? '0.82rem' : '0.9rem',
                    letterSpacing: '-0.01em',
                    color: 'rgba(26, 26, 46, 0.55)',
                    minHeight: 48,
                    transition: `color 250ms ${EASE}, transform 200ms ${EASE}`,
                    '&:hover': {
                        color: 'rgba(26, 26, 46, 0.8)',
                    },
                },
                '& .Mui-selected': {
                    color: `${BRAND_ORANGE} !important`,
                    fontWeight: 700,
                },
            }}
        >
            {tabs.map((t, i) => (
                <Tab key={i} label={t} />
            ))}
        </Tabs>
    );
}

/** Main V-Sécurité view. Renders admin or collab sub-view depending on userRoleId. */
export default function VSecuriteView() {
    const dispatch = useDispatch();
    const mounted = useRef(false);

    const userRoleId = useSelector((state) => state.login.userRoleId);
    const isMobile = useSelector((state) => state.settings.isMobile);
    const teams = useSelector((state) => state.vsecurite.awareness.teams);
    const awarenessCampaigns = useSelector((state) => state.vsecurite.awareness.campaigns);
    const lang = useSelector((state) => state.settings.lang);

    const isMobileScreen = isMobile === 2;

    // Role: 1 = superadmin, 2 = manager/admin, 3+ = collab
    const isAdmin = checkPermission(config.scopes.vsecurite, config.permissionTypes.edit);

    // ─── ADMIN state ────────────────────────────────────────────────────────────
    const [adminMainTab, setAdminMainTab] = useState(0); // 0=Sensibilisation, 1=Quiz, 2=Dashboard
    const [adminDialogOpen, setAdminDialogOpen] = useState(false);

    // Awareness admin sub-views: 'list' | 'form' | 'tracking'
    const [awarenessView, setAwarenessView] = useState('list');
    const [selectedCampaign, setSelectedCampaign] = useState(null);

    // Quiz admin sub-views: 'list' | 'form' | 'questions' | 'tracking' | 'import'
    const [quizView, setQuizView] = useState('list');
    const [selectedQuiz, setSelectedQuiz] = useState(null);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    /** True when the QuestionForm should be visible (add or edit mode) */
    const [showQuestionForm, setShowQuestionForm] = useState(false);

    // ─── COLLAB state ────────────────────────────────────────────────────────────
    const [collabMainTab, setCollabMainTab] = useState(0); // 0=Sensibilisation, 1=Quiz
    const [collabAwarenessSubTab, setCollabAwarenessSubTab] = useState(0); // 0=Pending, 1=History
    const [collabQuizSubTab, setCollabQuizSubTab] = useState(0); // 0=Pending, 1=History

    // Quiz flow state
    const [quizFlowActive, setQuizFlowActive] = useState(false);
    const [activeQuiz, setActiveQuiz] = useState(null);
    const [reviewQuizId, setReviewQuizId] = useState(null);
    const [reviewMode, setReviewMode] = useState(false);

    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
            if (isAdmin) {
                dispatch(fetchAwarenessCampaigns());
                dispatch(fetchAwarenessTeams());
                dispatch(fetchQuizzes());
            } else {
                dispatch(fetchCollabPendingAwareness());
                dispatch(fetchCollabAwarenessHistory());
                dispatch(fetchCollabPendingQuizzes());
                dispatch(fetchCollabQuizHistory());
            }
        }
    }, [dispatch, isAdmin]);

    // ─── ADMIN handlers ──────────────────────────────────────────────────────────

    /** Navigate to awareness tracking for a campaign */
    const handleSelectCampaignTracking = (campaign) => {
        setSelectedCampaign(campaign);
        setAwarenessView('tracking');
    };

    /** Navigate to awareness edit form */
    const handleEditCampaign = (campaign) => {
        setSelectedCampaign(campaign);
        setAwarenessView('form');
    };

    /** Navigate to awareness create form */
    const handleCreateCampaign = () => {
        setSelectedCampaign(null);
        setAwarenessView('form');
    };

    /** Return from awareness sub-view to list */
    const handleAwarenessBack = () => {
        setAwarenessView('list');
        setSelectedCampaign(null);
        dispatch(fetchAwarenessCampaigns());
    };

    /** Navigate to quiz question manager */
    const handleManageQuestions = (quiz) => {
        setSelectedQuiz(quiz);
        setSelectedQuestion(null);
        setShowQuestionForm(false);
        setQuizView('questions');
    };

    /** Navigate to quiz tracking dashboard */
    const handleSelectQuizTracking = (quiz) => {
        setSelectedQuiz(quiz);
        setQuizView('tracking');
    };

    /** Navigate to quiz edit form */
    const handleEditQuiz = (quiz) => {
        setSelectedQuiz(quiz);
        setQuizView('form');
    };

    /** Navigate to quiz create form */
    const handleCreateQuiz = () => {
        setSelectedQuiz(null);
        setQuizView('form');
    };

    /** Navigate to question import view */
    const handleOpenImport = () => {
        setQuizView('import');
    };

    /** Return from quiz sub-view to list */
    const handleQuizBack = () => {
        setQuizView('list');
        setSelectedQuiz(null);
        setSelectedQuestion(null);
        setShowQuestionForm(false);
        dispatch(fetchQuizzes());
    };

    // ─── COLLAB handlers ─────────────────────────────────────────────────────────

    /** Start a quiz attempt flow */
    const handleStartQuiz = (quiz) => {
        setActiveQuiz(quiz);
        setQuizFlowActive(true);
        setReviewMode(false);
    };

    /** Finish quiz flow and return to pending list */
    const handleQuizFlowFinish = () => {
        setQuizFlowActive(false);
        setActiveQuiz(null);
        dispatch(fetchCollabPendingQuizzes());
        dispatch(fetchCollabQuizHistory());
    };

    /** Open quiz review for a passed quiz */
    const handleReviewQuiz = (quizId) => {
        setReviewQuizId(quizId);
        setReviewMode(true);
        setQuizFlowActive(false);
    };

    /** Return from review back to history tab */
    const handleReviewBack = () => {
        setReviewMode(false);
        setReviewQuizId(null);
        setCollabQuizSubTab(1);
    };

    // ─── RENDER ──────────────────────────────────────────────────────────────────

    const containerSx = {
        p: isMobileScreen ? 1.5 : 3,
        mt: 0,
        minHeight: '100%',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        background: 'linear-gradient(135deg, #f8f9fb 0%, #f0f2f8 40%, #fdf5ee 100%)',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '280px',
            background: 'linear-gradient(180deg, rgba(255,144,21,0.03) 0%, transparent 100%)',
            pointerEvents: 'none',
            borderRadius: '0 0 40px 40px',
        },
    };

    // ── Admin view ───────────────────────────────────────────────────────────────
    if (isAdmin) {
        return (
            <Box sx={containerSx}>
                {/* Title row — superadmin gets a "Manage Admins" button */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2.5,
                    flexWrap: 'wrap',
                    gap: 1,
                }}>
                    <Typography variant="h5" sx={{
                        fontWeight: 700,
                        color: '#1a1a2e',
                        letterSpacing: '-0.03em',
                        fontSize: isMobileScreen ? '1.2rem' : '1.4rem',
                        position: 'relative',
                        zIndex: 1,
                        '@keyframes vsecFadeIn': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                        animation: `vsecFadeIn 400ms ${EASE} both`,
                    }}>
                        <Langs str="VSEC_VIEW_TITLE" />
                    </Typography>
                    {userRoleId === 1 && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AdminPanelSettingsRoundedIcon />}
                            onClick={() => setAdminDialogOpen(true)}
                            sx={{
                                borderColor: BRAND_ORANGE,
                                color: BRAND_ORANGE,
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.82rem',
                                minHeight: 44,
                                '&:hover': { bgcolor: 'rgba(255,144,21,0.06)', borderColor: BRAND_ORANGE },
                            }}
                        >
                            <Langs str="VSEC_ADMIN_MANAGE_BTN" />
                        </Button>
                    )}
                </Box>

                <VsecAdminDialog
                    open={adminDialogOpen}
                    onClose={() => setAdminDialogOpen(false)}
                    lang={lang}
                />

                <StyledTabs
                    value={adminMainTab}
                    onChange={(_, v) => { setAdminMainTab(v); setAwarenessView('list'); setQuizView('list'); }}
                    tabs={[Localize("VSEC_ADMIN_TAB_AWARENESS", lang), Localize("VSEC_ADMIN_TAB_QUIZ", lang), Localize("VSEC_ADMIN_TAB_DASHBOARD", lang)]}
                    isMobileScreen={isMobileScreen}
                />

                {/* ── ADMIN: Sensibilisation ── */}
                {adminMainTab === 0 && (
                    <>
                        {awarenessView === 'list' && (
                            <AwarenessList
                                onSelectCampaign={handleSelectCampaignTracking}
                                onEditCampaign={handleEditCampaign}
                                onCreateNew={handleCreateCampaign}
                            />
                        )}
                        {awarenessView === 'form' && (
                            <AwarenessForm
                                campaign={selectedCampaign}
                                teams={teams}
                                onSave={handleAwarenessBack}
                                onCancel={handleAwarenessBack}
                            />
                        )}
                        {awarenessView === 'tracking' && selectedCampaign && (
                            <AwarenessTracking
                                campaign={selectedCampaign}
                                onBack={handleAwarenessBack}
                            />
                        )}
                    </>
                )}

                {/* ── ADMIN: Quiz ── */}
                {adminMainTab === 1 && (
                    <>
                        {quizView === 'list' && (
                            <QuizList
                                onSelectQuiz={handleSelectQuizTracking}
                                onEditQuiz={handleEditQuiz}
                                onCreateNew={handleCreateQuiz}
                                onManageQuestions={handleManageQuestions}
                            />
                        )}
                        {quizView === 'form' && (
                            <QuizForm
                                quiz={selectedQuiz}
                                campaigns={awarenessCampaigns}
                                teams={teams}
                                onSave={handleQuizBack}
                                onCancel={handleQuizBack}
                            />
                        )}
                        {quizView === 'questions' && selectedQuiz && (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                height: isMobileScreen ? 'calc(100vh - 160px)' : 'calc(100vh - 200px)',
                                maxHeight: isMobileScreen ? 'calc(100vh - 160px)' : 'calc(100vh - 200px)',
                                overflow: 'hidden',
                            }}>
                                {/* Sticky header bar */}
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: isMobileScreen ? 'flex-start' : 'center',
                                    flexDirection: isMobileScreen ? 'column' : 'row',
                                    gap: 1,
                                    flexShrink: 0,
                                    pb: 2,
                                    borderBottom: '1px solid rgba(0, 40, 128, 0.07)',
                                    mb: 0,
                                    background: 'rgba(255, 255, 255, 0.5)',
                                    backdropFilter: 'blur(8px)',
                                    WebkitBackdropFilter: 'blur(8px)',
                                    borderRadius: '12px',
                                    px: isMobileScreen ? 1.5 : 2,
                                    pt: 1.5,
                                }}>
                                    <Typography variant="h6" sx={{
                                        fontWeight: 700,
                                        flex: 1,
                                        color: '#1a1a2e',
                                        fontSize: isMobileScreen ? '0.95rem' : undefined,
                                        lineHeight: 1.3,
                                        width: isMobileScreen ? '100%' : 'auto',
                                    }}>
                                        {showQuestionForm
                                            ? (selectedQuestion
                                                ? Localize("VSEC_QUESTIONS_EDIT_TITLE", lang)
                                                : Localize("VSEC_QUESTIONS_ADD_TITLE", lang))
                                            : `${Localize("VSEC_QUESTIONS_TITLE", lang)} ${selectedQuiz.title || selectedQuiz.titre}`
                                        }
                                    </Typography>
                                    <Box sx={{
                                        display: 'flex',
                                        gap: 1,
                                        width: isMobileScreen ? '100%' : 'auto',
                                        flexDirection: isMobileScreen ? 'column' : 'row',
                                    }}>
                                        {!showQuestionForm && selectedQuiz?.status !== 'published' && selectedQuiz?.statut !== 'published' && (
                                            <Button
                                                variant="outlined"
                                                onClick={handleOpenImport}
                                                fullWidth={isMobileScreen}
                                                sx={{
                                                    bgcolor: 'white',
                                                    color: BRAND_ORANGE,
                                                    border: `1px solid ${BRAND_ORANGE}`,
                                                    borderRadius: '10px',
                                                    px: 2,
                                                    py: 0.75,
                                                    fontWeight: 600,
                                                    fontSize: 14,
                                                    textTransform: 'none',
                                                    minHeight: 44,
                                                    transition: `all 200ms ${EASE}`,
                                                    '&:hover': {
                                                        bgcolor: 'rgba(255, 144, 21, 0.06)',
                                                        borderColor: BRAND_ORANGE,
                                                        transform: 'scale(1.02)',
                                                    },
                                                    '&:active': { transform: 'scale(0.98)' },
                                                }}
                                            >
                                                {Localize("VSEC_BTN_IMPORT_EXCEL", lang)}
                                            </Button>
                                        )}
                                        <Button
                                            variant="outlined"
                                            onClick={handleQuizBack}
                                            fullWidth={isMobileScreen}
                                            sx={{
                                                bgcolor: 'white',
                                                color: '#666',
                                                border: '1px solid #ccc',
                                                borderRadius: '10px',
                                                px: 2,
                                                py: 0.75,
                                                fontWeight: 600,
                                                fontSize: 14,
                                                textTransform: 'none',
                                                minHeight: 44,
                                                transition: `all 200ms ${EASE}`,
                                                '&:hover': {
                                                    bgcolor: 'rgba(0, 0, 0, 0.03)',
                                                    borderColor: '#ccc',
                                                    transform: 'scale(1.02)',
                                                },
                                                '&:active': { transform: 'scale(0.98)' },
                                            }}
                                        >
                                            {Localize("VSEC_BTN_BACK_QUIZZES", lang)}
                                        </Button>
                                    </Box>
                                </Box>

                                {/* Scrollable content area */}
                                <Box sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflowY: 'auto',
                                    pt: 2,
                                    WebkitOverflowScrolling: 'touch',
                                    '&::-webkit-scrollbar': { width: '6px' },
                                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: 'rgba(255, 144, 21, 0.18)',
                                        borderRadius: '3px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: 'rgba(255, 144, 21, 0.35)',
                                    },
                                }}>
                                    {/* Question form — shown when adding or editing */}
                                    {showQuestionForm ? (
                                        <QuestionForm
                                            question={selectedQuestion}
                                            quizId={selectedQuiz.id}
                                            onSave={() => {
                                                setSelectedQuestion(null);
                                                setShowQuestionForm(false);
                                            }}
                                            onCancel={() => {
                                                setSelectedQuestion(null);
                                                setShowQuestionForm(false);
                                            }}
                                        />
                                    ) : (
                                        /* Questions list — shown by default */
                                        <QuizQuestionsList
                                            quizId={selectedQuiz.id}
                                            isPublished={selectedQuiz?.status === 'published' || selectedQuiz?.statut === 'published'}
                                            onAddNew={() => {
                                                setSelectedQuestion(null);
                                                setShowQuestionForm(true);
                                            }}
                                            onEditQuestion={(q) => {
                                                setSelectedQuestion(q);
                                                setShowQuestionForm(true);
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        )}
                        {quizView === 'tracking' && selectedQuiz && (
                            <QuizTracking
                                quiz={selectedQuiz}
                                onBack={handleQuizBack}
                            />
                        )}
                        {quizView === 'import' && selectedQuiz && (
                            <Box sx={{
                                maxHeight: isMobileScreen ? 'calc(100vh - 160px)' : 'calc(100vh - 200px)',
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                '&::-webkit-scrollbar': { width: '6px' },
                                '&::-webkit-scrollbar-track': { background: 'transparent' },
                                '&::-webkit-scrollbar-thumb': {
                                    background: 'rgba(255, 144, 21, 0.18)',
                                    borderRadius: '3px',
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    background: 'rgba(255, 144, 21, 0.35)',
                                },
                            }}>
                                <QuizImport
                                    quizId={selectedQuiz.id}
                                    onImportSuccess={() => setQuizView('questions')}
                                    onCancel={() => setQuizView('questions')}
                                />
                            </Box>
                        )}
                    </>
                )}

                {/* ── ADMIN: Dashboard ── */}
                {adminMainTab === 2 && (
                    <VSecuriteDashboard />
                )}
            </Box>
        );
    }

    // ── Collab view ──────────────────────────────────────────────────────────────
    return (
        <Box sx={containerSx}>
            <Typography variant="h5" sx={{
                fontWeight: 700,
                mb: 2.5,
                color: '#1a1a2e',
                letterSpacing: '-0.03em',
                fontSize: isMobileScreen ? '1.2rem' : '1.4rem',
                position: 'relative',
                zIndex: 1,
                '@keyframes vsecFadeIn': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                animation: `vsecFadeIn 400ms ${EASE} both`,
            }}>
                <Langs str="VSEC_VIEW_TITLE" />
            </Typography>

            <StyledTabs
                value={collabMainTab}
                onChange={(_, v) => {
                    setCollabMainTab(v);
                    setQuizFlowActive(false);
                    setReviewMode(false);
                }}
                tabs={[Localize("VSEC_COLLAB_TAB_AWARENESS", lang), Localize("VSEC_COLLAB_TAB_QUIZ", lang)]}
                isMobileScreen={isMobileScreen}
            />

            {/* ── COLLAB: Sensibilisation ── */}
            {collabMainTab === 0 && (
                <>
                    <StyledTabs
                        value={collabAwarenessSubTab}
                        onChange={(_, v) => setCollabAwarenessSubTab(v)}
                        tabs={[Localize("VSEC_COLLAB_TAB_PENDING", lang), Localize("VSEC_COLLAB_TAB_HISTORY", lang)]}
                        isMobileScreen={isMobileScreen}
                    />
                    {collabAwarenessSubTab === 0 && <AwarenessCollab />}
                    {collabAwarenessSubTab === 1 && <AwarenessHistory />}
                </>
            )}

            {/* ── COLLAB: Quiz ── */}
            {collabMainTab === 1 && (
                <>
                    {/* Quiz taking flow — full-screen Dialog on mobile, inline on desktop */}
                    {quizFlowActive && activeQuiz && (
                        isMobileScreen ? (
                            <Dialog
                                open
                                fullScreen
                                slots={{ transition: SlideUp }}
                                sx={{ zIndex: 1400 }}
                                slotProps={{ paper: { sx: { bgcolor: 'transparent', boxShadow: 'none' } } }}
                            >
                                <QuizTake
                                    quiz={activeQuiz}
                                    onFinish={handleQuizFlowFinish}
                                    onBack={handleQuizFlowFinish}
                                />
                            </Dialog>
                        ) : (
                            <QuizTake
                                quiz={activeQuiz}
                                onFinish={handleQuizFlowFinish}
                                onBack={handleQuizFlowFinish}
                            />
                        )
                    )}

                    {/* Quiz review flow — full-screen Dialog on mobile, inline on desktop */}
                    {!quizFlowActive && reviewMode && reviewQuizId && (
                        isMobileScreen ? (
                            <Dialog
                                open
                                fullScreen
                                slots={{ transition: SlideUp }}
                                sx={{ zIndex: 1400 }}
                                slotProps={{ paper: { sx: { bgcolor: '#f7f8fa', overflowY: 'auto' } } }}
                            >
                                <QuizReview
                                    quizId={reviewQuizId}
                                    onBack={handleReviewBack}
                                />
                            </Dialog>
                        ) : (
                            <Box sx={{
                                maxHeight: 'calc(100vh - 160px)',
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                '&::-webkit-scrollbar': { width: '5px' },
                                '&::-webkit-scrollbar-track': { background: 'transparent' },
                                '&::-webkit-scrollbar-thumb': { background: 'rgba(255,144,21,0.25)', borderRadius: '3px' },
                            }}>
                                <QuizReview
                                    quizId={reviewQuizId}
                                    onBack={handleReviewBack}
                                />
                            </Box>
                        )
                    )}

                    {/* Normal quiz list / history */}
                    {!quizFlowActive && !reviewMode && (
                        <>
                            <StyledTabs
                                value={collabQuizSubTab}
                                onChange={(_, v) => setCollabQuizSubTab(v)}
                                tabs={[Localize("VSEC_COLLAB_TAB_PENDING", lang), Localize("VSEC_COLLAB_TAB_HISTORY", lang)]}
                                isMobileScreen={isMobileScreen}
                            />
                            {collabQuizSubTab === 0 && (
                                <QuizCollab onStartQuiz={handleStartQuiz} />
                            )}
                            {collabQuizSubTab === 1 && (
                                <QuizHistory onReviewQuiz={handleReviewQuiz} />
                            )}
                        </>
                    )}
                </>
            )}
        </Box>
    );
}
